import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Container } from '../ui/Container';
interface PageShellProps {
  title: string;
  eyebrow?: string;
  intro?: string;
  image?: string;
  imageAlt?: string;
  heroBackground?: {
    src: string;
    alt: string;
    position: string;
    mobilePosition: string;
    credit?: string;
  };
  heroAside?: ReactNode;
  breadcrumbs?: {
    label: string;
    href?: string;
  }[];
  children: ReactNode;
}
export function PageShell({
  title,
  eyebrow = 'Bates Taekwondo',
  intro,
  image,
  imageAlt = '',
  heroBackground,
  heroAside,
  breadcrumbs,
  children
}: PageShellProps) {
  return <div className="min-h-screen bg-paper text-ink" data-id="element-456" data-mp="m1sovheo0n9i">
      <Header data-id="element-457" data-mp="m07y82vh1ogx" />
      <main id="main" tabIndex={-1} data-id="element-458" data-mp="lzprudf3htva">
        <section className={`relative isolate overflow-hidden border-b border-line py-12 sm:py-16 lg:py-20 ${heroBackground ? 'program-hero on-dark bg-coal text-white' : 'bg-white'}`} data-id="element-459" data-mp="3uhcem6g4nnjt">
          {heroBackground && <>
            <img src={heroBackground.src} alt={heroBackground.alt} className="program-hero-photo absolute inset-0 h-full w-full object-cover" style={{
            '--hero-position': heroBackground.position,
            '--hero-mobile-position': heroBackground.mobilePosition
          } as CSSProperties} data-id="element-460" data-mp="1xv2flv911de0" />
            <div className="program-hero-shade absolute inset-0" aria-hidden="true" data-id="element-461" data-mp="2bpmo3ifh6ihb" />
          </>}
          <Container className={`relative z-10 ${heroAside ? 'grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:gap-14' : image ? 'grid items-center gap-9 lg:grid-cols-2 lg:gap-14' : ''}`} data-id="element-462" data-mp="1ezccu5t9scz2">
            <div className="min-w-0" data-id="element-463" data-mp="1o0ah8p7memi6">
              {breadcrumbs ? <nav aria-label="Breadcrumb" className={`mb-7 text-sm font-medium ${heroBackground ? 'text-white/90' : 'text-muted'}`} data-id="element-464" data-mp="1syc0pb8yknu6">
                  <ol className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-465" data-mp="34c7ikrr8zap3">
                    {breadcrumbs.map((crumb, index) => <li key={crumb.label} className="flex min-h-[44px] items-center gap-3" data-id="element-466" data-mp="2en0kz5toe7k3" data-mpi={index}>
                        {index > 0 && <span aria-hidden="true" data-id="element-467" data-mp="29o5y9ssl7yup">›</span>}
                        {crumb.href ? <a href={crumb.href} className={`inline-flex min-h-[44px] items-center underline underline-offset-4 ${heroBackground ? 'hover:text-white' : 'hover:text-ink'}`} data-id="element-468" data-mp="2okwujt6ysug3">{crumb.label}</a> : <span aria-current="page" data-id="element-469" data-mp="1vv7ubi22wyfj">{crumb.label}</span>}
                      </li>)}
                  </ol>
                </nav> : <a href="/" className={`mb-7 inline-flex min-h-[44px] items-center text-sm font-medium underline underline-offset-4 ${heroBackground ? 'text-white/80 hover:text-white' : 'text-muted hover:text-ink'}`} data-id="element-470" data-mp="20wnfrb8656yg">Home</a>}
              <p className={`mb-4 text-sm font-semibold uppercase tracking-[0.15em] ${heroBackground ? 'text-white/90' : 'text-brand'}`} data-id="element-471" data-mp="186x1912mk31t">{eyebrow}</p>
              <h1 className="max-w-4xl text-balance font-display text-[3.1rem] font-bold uppercase leading-[0.95] sm:text-[4rem] lg:text-[4.75rem]" data-id="element-472" data-mp="3jtibyiz2av5v">{title}</h1>
              {intro && <p className={`text-pretty mt-6 max-w-[43rem] text-lg leading-relaxed ${heroBackground ? 'text-white/90' : 'text-muted'}`} data-id="element-473" data-mp="39yqi9q8losgd">{intro}</p>}
            </div>
            {heroAside && <div className="mx-auto w-full min-w-0 max-w-[440px] lg:ml-auto lg:mr-0" data-id="element-474" data-mp="6hgnpbsv4sro">{heroAside}</div>}
            {image && <img src={image} alt={imageAlt} className={`aspect-[4/3] w-full rounded object-cover ${heroAside ? 'lg:col-span-2' : ''}`} data-id="element-475" data-mp="bf0006602vgz" />}
          </Container>
          {heroBackground?.credit && <p className="absolute bottom-3 right-5 z-10 rounded bg-black/60 px-2 py-1 text-[10px] text-white/90 sm:right-8" data-id="element-476" data-mp="1gaulcvaj3hm">{heroBackground.credit}</p>}
        </section>
        <Container className="space-y-14 py-12 sm:space-y-16 sm:py-16 lg:py-20" data-id="element-477" data-mp="2m7bc1mdpdqzu">{children}</Container>
      </main>
      <Footer data-id="element-478" data-mp="32ezbe4yetpo5" />
    </div>;
}